import { type FormEvent, useState } from 'react';
import type { Api } from '../api.js';
import type { TrackingDimension } from './types.js';
import { useOrgData } from './useOrgData.js';

export function Tracking({ api, orgId }: { api: Api; orgId: string }) {
  const { data, error, reload } = useOrgData<{ dimensions: TrackingDimension[] }>(
    api,
    orgId,
    '/tracking',
  );
  const [name, setName] = useState('Product');
  const [values, setValues] = useState('');
  const [required, setRequired] = useState(true);
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setActionError('');
    try {
      await api.send(orgId, 'POST', '/tracking', {
        name,
        position: (data?.dimensions.length ?? 0) + 1,
        requiredOnPl: required,
        values: values
          .split(/[,\n]/)
          .map((v) => v.trim())
          .filter(Boolean),
      });
      setValues('');
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack">
      <h2>Tracking</h2>
      {error && <p className="error">{error}</p>}
      {data?.dimensions.map((d) => (
        <div key={d.id}>
          <strong>{d.name}</strong>{' '}
          {d.required_on_pl && (
            <span className="muted">(required on income and expense lines)</span>
          )}
          <p>{d.values.map((v) => v.name).join(', ')}</p>
        </div>
      ))}
      {data && data.dimensions.length < 2 && (
        <form onSubmit={create} className="stack">
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Values, separated by commas
            <input
              value={values}
              onChange={(e) => setValues(e.target.value)}
              placeholder="OnReplay, LobsterOne, OnSilent"
              required
            />
          </label>
          <label className="inline">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Required on income and expense lines
          </label>
          {actionError && <p className="error">{actionError}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Saving' : 'Add'}
          </button>
        </form>
      )}
    </section>
  );
}
